<template>
  <div style="margin: 0 20px 20px 20px">
    <FiPageRail title="Settings History" compact />

    <!-- date search laid out like the actuals toolbar: preset chips and a From/To
         cluster with double arrows shifting by the loaded window and single arrows
         by a day. presets and arrows reload on their own; a hand-picked From/To
         waits for Search so that setting two dates fires one query instead of
         one per keystroke. -->
    <div class="actuals-cm-toolbar settings-history-toolbar">
      <div class="actuals-date-presets" role="group" aria-label="Settings history date range">
        <span class="actuals-range-label">Range</span>
        <button
          v-for="opt in quick_range_options"
          :key="'qr-' + opt.value"
          type="button"
          :class="['actuals-date-chip', {'actuals-date-chip--active': active_preset === opt.value}]"
          :aria-pressed="active_preset === opt.value ? 'true' : 'false'"
          :title="opt.title"
          v-on:click.prevent="set_quick_range(opt.value)"
        >{{ opt.label }}</button>
      </div>
      <div class="actuals-filter-dates actuals-filter-dates--open">
        <span class="actuals-filter-dateunit">
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift back one window" v-on:click.prevent="nudge_range_days(-day_count)"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Previous day" v-on:click.prevent="nudge_range_days(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
          </b-button-group>
          <span class="actuals-filter-field">
            <span class="actuals-range-label">From</span>
            <b-form-input v-model="from_date_iso" type="date" size="sm" class="actuals-filter-date" :max="today_iso"/>
          </span>
        </span>
        <span class="actuals-filter-dateunit">
          <span class="actuals-filter-field">
            <span class="actuals-range-label">To</span>
            <b-form-input v-model="to_date_iso" type="date" size="sm" class="actuals-filter-date" :max="today_iso"/>
          </span>
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Next day" :disabled="!can_shift_forward" v-on:click.prevent="nudge_range_days(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift forward one window" :disabled="!can_shift_forward" v-on:click.prevent="nudge_range_days(day_count)"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
          </b-button-group>
        </span>
      </div>
      <b-button
        size="sm"
        :variant="range_pending ? 'primary' : 'secondary'"
        :title="range_pending ? 'Search the picked date range -- the results below are still the previous range' : 'Search'"
        v-on:click.prevent="load_data()"
      >Search</b-button>
      <span class="actuals-filter-field">
        <span class="actuals-range-label">Filter Results</span>
        <b-form-input size="sm" class="settings-history-text-filter" v-model="text_filter" placeholder=""/>
      </span>
    </div>

    <div v-if="!loading">
      <div class="row" style="margin: 30px 10px 30px 10px; font-weight: bold; font-size: 22px;">
        <div class="col" style="border: 1px solid grey;">Total: {{items_full.length}}</div>
        <div class="col" style="border: 1px solid grey;">Filtered: {{items.length}}</div>
      </div>
    </div>

    <div>
      <b-table :busy="loading" sticky-header="70vh" bordered :items="items" :fields="fields"
               :current-page="currentPage" :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeSmallYear}}
        </template>

        <template slot="updated_by" slot-scope="row">
          <div>{{ format_updated_by(row.item.updated_by) }}</div>
          <div v-if="display_name_differs(row.item.updated_by)" class="updated-by-username">
            {{ row.item.updated_by }}
          </div>
        </template>

        <template slot="diff" slot-scope="row">
          <settings-history-diff-cell :item="row.item" />
        </template>

      </b-table>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>

import {get_tenant_config_history, get_users} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment'
import settings_history_diff_cell from './settings_history_diff_cell.vue'

export default {
  name: 'tenant_config_history',
  components: {
    'settings-history-diff-cell': settings_history_diff_cell,
  },

  data() {
    return {
      text_filter: '',
      items: [],
      items_full: [],
      summary: [],
      loading: true,
      from_date: moment().subtract(6, 'days').format('YYYY-MM-DD'),
      to_date: moment().format('YYYY-MM-DD'),
      // the window the table is showing. a hand-picked date only moves the two
      // above, so this lags them until Search is pressed
      loaded_key: '',
      quick_range_options: [
        { value: 1, label: '1d', title: 'Today' },
        { value: 7, label: '7d', title: 'Last 7 days' },
        { value: 30, label: '30d', title: 'Last 30 days' },
        { value: 90, label: '90d', title: 'Last 90 days' },
      ],
      booking_type: '',
      totalRows: 1,
      currentPage: 1,
      perPage: 100,
      pages: [],
      fields_all: [],
      fields: [
        { key: 'processed_time', sortable: true, label: 'When', thStyle: { width: "150px" }},
        { key: 'updated_by', sortable: true, label: 'Updated by', thStyle: { width: "180px" }},
        { key: 'diff', sortable: false, label: 'Changes'},
      ],
      users_by_username: {},
    }
  },

  async created() {
    this.fields_all = this.fields
    this.check_features()
  },
  methods: {
    async check_features() {
      this.load_data()
    },
    async load_data() {
      this.loading = true
      try {
        // Load users in parallel; failure here is non-fatal because the
        // history table will still render with raw usernames.
        const range_key = this.range_key
        const history_promise = get_tenant_config_history(
          this.user.tenant,
          this.from_date,
          this.to_date,
        )
        const users_promise = get_users(this.user.tenant).catch(() => null)

        const [history_resp, users_resp] = await Promise.all([history_promise, users_promise])
        this.users_by_username = this.build_users_map(users_resp)

        const {value: { data = {} } } = history_resp
        this.summary = data.summary || {}
        moment.tz.setDefault(this.summary.timezone)
        const records = data.records || []
        this.items = records.map(record => this.normalize_history_record(record))
        this.items_full = this.items
        this.loaded_key = range_key
        this.filter_results()
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    set_quick_range(days) {
      this.from_date = moment().subtract(days - 1, 'days').format('YYYY-MM-DD')
      this.to_date = moment().format('YYYY-MM-DD')
      this.load_data()
    },
    // the double arrows pass the loaded window, the single arrows a day. forward
    // shifts stop at today rather than sliding the window into an empty future
    nudge_range_days(days) {
      const room = moment(this.today_iso, 'YYYY-MM-DD').diff(moment(this.to_date, 'YYYY-MM-DD'), 'days')
      const shift = days > 0 ? Math.min(days, room) : days
      if (!shift)
        return
      this.from_date = moment(this.from_date, 'YYYY-MM-DD').add(shift, 'days').format('YYYY-MM-DD')
      this.to_date = moment(this.to_date, 'YYYY-MM-DD').add(shift, 'days').format('YYYY-MM-DD')
      this.load_data()
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    filter_results() {
      this.items = this.items_full
      if (this.text_filter != '') {
        const val = this.text_filter
        this.items = this.items.filter(item =>
          this.contains(JSON.stringify(item.diff), val) ||
          this.contains(item.htmldiff, val) ||
          this.contains(item.updated_by, val) ||
          this.contains(this.format_updated_by(item.updated_by), val) ||
          this.contains(JSON.stringify(item.old), val) ||
          this.contains(JSON.stringify(item.new), val)
        )
      }
    },

    normalize_history_record(record) {
      if (!record) return record
      const normalized = { ...record }
      if (normalized.diff != null && typeof normalized.diff !== 'string') {
        try {
          normalized.diff = JSON.stringify(normalized.diff)
        } catch (e) {
          normalized.diff = String(normalized.diff)
        }
      }
      if (normalized.htmldiff != null && typeof normalized.htmldiff !== 'string') {
        try {
          normalized.htmldiff = JSON.stringify(normalized.htmldiff)
        } catch (e) {
          normalized.htmldiff = String(normalized.htmldiff)
        }
      }
      return normalized
    },
    build_users_map(users_resp) {
      const map = {}
      try {
        const results = users_resp && users_resp.value && users_resp.value.data
          ? (users_resp.value.data.results || [])
          : []
        for (const u of results) {
          if (u && u.username) {
            map[u.username.toLowerCase()] = u
          }
        }
      } catch (e) {
        // best-effort lookup; fall back to username if anything goes wrong
      }
      return map
    },
    format_updated_by(username) {
      if (!username) return ''
      const user = this.users_by_username[String(username).toLowerCase()]
      if (!user) return username
      const first = (user.first_name || '').trim()
      const last = (user.last_name || '').trim()
      const full = [first, last].filter(Boolean).join(' ')
      return full || username
    },
    display_name_differs(username) {
      if (!username) return false
      const formatted = this.format_updated_by(username)
      return formatted && formatted !== username
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },

  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    is_admin() {
      return this.user.role === 'admin'
    },
    telematics_type() {
      return this.user.telematics_types[this.user.tenant]
    },
    tenant_type() {
      return this.user.types[this.user.tenant]
    },
    today_iso() {
      return moment().format('YYYY-MM-DD')
    },
    // native date inputs write here. writes that clear the field or change
    // nothing are ignored, so the range math never sees an empty date
    from_date_iso: {
      get() { return this.from_date },
      set(v) {
        if (!v || v === this.from_date) return
        this.from_date = v
      },
    },
    to_date_iso: {
      get() { return this.to_date },
      set(v) {
        if (!v || v === this.to_date) return
        this.to_date = v
      },
    },
    // the loaded window in calendar days, inclusive: 7d is seven days, so the
    // double arrows step to the adjacent window with no overlap
    day_count() {
      return Math.max(1, moment(this.to_date, 'YYYY-MM-DD').diff(moment(this.from_date, 'YYYY-MM-DD'), 'days') + 1)
    },
    // a preset is "active" when the window happens to match it, so a hand-picked
    // range lights nothing rather than needing a mode to sit in
    active_preset() {
      if (this.to_date !== this.today_iso) return null
      const match = this.quick_range_options.find(opt => opt.value === this.day_count)
      return match ? match.value : null
    },
    range_key() {
      return this.from_date + '|' + this.to_date
    },
    // a hand-picked date leaves the table on the previous window, so the Search
    // button carries the fact that the two no longer agree
    range_pending() {
      return !!this.loaded_key && this.range_key !== this.loaded_key
    },
    can_shift_forward() {
      return this.to_date < this.today_iso
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.check_features()
      }
    },
    text_filter() {
      this.filter_results()
    },
    // ISO dates order as strings, so the from-after-to clamp needs no parsing
    from_date(v) {
      if (v > this.to_date) this.to_date = v
    },
    to_date(v) {
      if (v < this.from_date) this.from_date = v
    },
  }
}
</script>

<style>
.error { background-color: rgba(255,0,0,0.25)}
.success { background-color: hsla(120,100%,75%,0.3)}
.unprocessed { background-color: hsla(200,40%,80%,0.3) }
.cancelled { background-color: hsla(290,60%,70%,0.3) }

.error td { background-color: rgba(255,0,0,0.25)}
.success td {background-color: hsla(120,100%,75%,0.3)}
.unprocessed td { background-color: hsla(200,40%,80%,0.3) }
.cancelled td { background-color: hsla(290,60%,70%,0.3) }


div.row:nth-child(even) {
  background-color: #f2f2f2;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.header {
  font-weight: bold;
}

div.col {
  border: 1px solid grey;
}

.table td {
  padding: .1rem !important;
}

.mvmt-table td {
  vertical-align: middle !important;
}

div.wrap {
  border: 1px solid grey;
  flex-wrap: wrap;
  display: flex;
  inline-size: 150px;
  /* overflow-wrap: break-word; */
}

recipients-list {
  white-space: break-spaces !important;
}

.updated-by-username {
  font-size: 11px;
  color: #888;
  margin-top: 2px;
  word-break: break-all;
}

</style>

<style scoped>
/* the actuals toolbar classes are inline-flex and take their alignment from an
   ancestor this page does not have, so the row's own layout is set here */
.settings-history-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 10px;
  margin: 20px 10px 10px;
}

.settings-history-toolbar .toolbar-date-shifts {
  flex-shrink: 0;
}

.settings-history-toolbar .toolbar-date-shift-btn {
  padding: 0.15rem 0.35rem;
  line-height: 1;
  min-width: 1.65rem;
}

.settings-history-toolbar .settings-history-text-filter {
  flex: 0 0 220px;
  width: 220px;
}
</style>
